import React, { useMemo, useState } from 'react';
import { Tooltip } from 'antd';
import { NavLink } from 'react-router-dom';
import InformationOutlineIcon from 'mdi-react/InformationOutlineIcon';
import { routeNames } from '../../routeSegments';
import useModule from '../../hooks/useModule';

const MAX_NAME_LENGTH = 45;

const ShareholderName = ({ name }) => {
  const [hovered, setHovered] = useState(false);
  if (name.length <= MAX_NAME_LENGTH) return name;
  return (
    // Hover anywhere on the name opens the tooltip, but it stays anchored to the info icon.
    <span
      className="inline-flex items-center gap-1 cursor-pointer"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {`${name.slice(0, MAX_NAME_LENGTH).trimEnd()}...`}
      <Tooltip
        title={name}
        open={hovered}
        placement="top"
        color="#FFFFFF"
        overlayStyle={{ maxWidth: 420 }}
        overlayInnerStyle={{ color: '#000000', whiteSpace: 'normal' }}
      >
        <span className="inline-flex shrink-0">
          <InformationOutlineIcon size={15} color="#BC8B20" />
        </span>
      </Tooltip>
    </span>
  );
};

const numFormatter = (number) => {
  const rounded = Math.round(number);
  return isNaN(rounded) ? "" : rounded.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

const getShareTypeText = (row) => `${row?.share_type ?? ''} ${row?.share_subtype ?? ''}`.toLowerCase();

const getShareCategory = (row) => {
  const type = getShareTypeText(row);
  if (type.includes('debenture') || isCcd(row)) return 'debentures';
  if (type.includes('prefer')) return 'preference';
  if (type.includes('equity')) return 'equity';
  return null;
};

// Compulsorily convertible debentures: shown under Debentures but kept out of Total Shares / Stake Held.
const isCcd = (row) => {
  const type = getShareTypeText(row);
  return /\bccds?\b/.test(type) || (type.includes('compulsor') && type.includes('convertible') && type.includes('debenture'));
};

const CCD_NOTE = 'CCDs are excluded from the total share count, as the conversion ratio is unknown.';

const PROMOTERS = 'Promoters';
const PUBLIC = 'Public';
const GROUP_ORDER = [PROMOTERS, PUBLIC];

// Only rows explicitly classified as promoter are Promoters; everything else (including blank) is Public.
const getGroup = (row) => {
  const normalized = `${row?.classification ?? ''}`.toLowerCase().replace(/[\s_-]/g, '');
  return normalized.includes('promoter') && !normalized.includes('nonpromoter') ? PROMOTERS : PUBLIC;
};

const columns = [
  { key: 'name', label: 'Shareholder Name', className: 'text-left' },
  { key: 'equity', label: 'Equity', className: 'text-right w-[14%]' },
  { key: 'preference', label: 'Preference Shares', className: 'text-right w-[14%]' },
  { key: 'debentures', label: 'Debentures', className: 'text-right w-[14%]' },
  { key: 'total', label: 'Total Shares', className: 'text-right w-[14%]' },
  { key: 'stake', label: 'Stake Held', className: 'text-right w-[12%]' },
];

/**
 * Builds the MGT-7 cap table from the CFS financials API rows
 * ({ shareholder_name, classification, share_type, share_subtype, shares_held, investor_id }):
 * grouped by classification (Promoters / Public) with one row per shareholder, plus the Total row.
 * Shared by the on-screen table and the Excel export so both always show the same numbers.
 */
export const buildMgt7CapTable = (data) => {
    const byGroup = new Map();
    data?.forEach((row) => {
      const group = getGroup(row);
      const name = row?.shareholder_name?.trim() || '--';
      if (!byGroup.has(group)) byGroup.set(group, new Map());
      const shareholders = byGroup.get(group);
      if (!shareholders.has(name)) {
        shareholders.set(name, { name, investorId: null, equity: 0, preference: 0, debentures: 0, total: 0 });
      }
      const entry = shareholders.get(name);
      if (!entry.investorId && row?.investor_id) entry.investorId = row.investor_id;
      const shares = parseFloat(row?.shares_held) || 0;
      const category = getShareCategory(row);
      if (category) entry[category] += shares;
      if (!isCcd(row)) entry.total += shares;
    });

    let grandTotal = 0;
    byGroup.forEach((shareholders) => shareholders.forEach((r) => { grandTotal += r.total; }));
    const withStake = (r) => ({ ...r, stake: grandTotal ? (r.total / grandTotal) * 100 : 0 });

    const orderOf = (name) => {
      const index = GROUP_ORDER.indexOf(name);
      return index === -1 ? GROUP_ORDER.length : index;
    };

    const groups = Array.from(byGroup.entries())
      .sort(([a], [b]) => orderOf(a) - orderOf(b))
      .map(([name, shareholders]) => {
        const rows = Array.from(shareholders.values()).map(withStake).sort((a, b) => b.total - a.total);
        const summary = rows.reduce(
          (acc, r) => ({
            ...acc,
            equity: acc.equity + r.equity,
            preference: acc.preference + r.preference,
            debentures: acc.debentures + r.debentures,
            total: acc.total + r.total,
          }),
          { name, equity: 0, preference: 0, debentures: 0, total: 0 }
        );
        return { name, rows, summary: withStake(summary) };
      });

    const totalRow = groups.reduce(
      (acc, { summary }) => ({
        ...acc,
        equity: acc.equity + summary.equity,
        preference: acc.preference + summary.preference,
        debentures: acc.debentures + summary.debentures,
        total: acc.total + summary.total,
        stake: acc.stake + summary.stake,
      }),
      { name: 'Total', equity: 0, preference: 0, debentures: 0, total: 0, stake: 0 }
    );

    return { groups, totalRow, hasCcd: data?.some(isCcd) ?? false };
};

const toExportRow = (level, row) => ({
  level,
  name: row.name,
  equity: row.equity,
  preference: row.preference,
  debentures: row.debentures,
  total: row.total,
  stake: Number(row.stake.toFixed(2)),
});

/** Flattens the MGT-7 table (all groups expanded) into the payload the cap_table_mgt7 Excel export expects. */
export const buildMgt7ExportPayload = (data) => {
  const { groups, totalRow, hasCcd } = buildMgt7CapTable(data);
  const rows = groups.flatMap((group) => [
    toExportRow('group', group.summary),
    ...group.rows.map((row) => toExportRow('shareholder', row)),
  ]);
  if (rows.length) rows.push(toExportRow('total', totalRow));
  return { rows, note: hasCcd ? CCD_NOTE : null };
};

const CfsFinancialsCapTable = ({ data }) => {
  const [expandedGroups, setExpandedGroups] = useState([]);
  const { mappedPrimaryMasterId } = useModule();
  const { groups, totalRow, hasCcd } = useMemo(() => buildMgt7CapTable(data), [data]);

  const toggleGroup = (name) => {
    setExpandedGroups((prev) => (prev.includes(name) ? prev.filter((g) => g !== name) : [...prev, name]));
  };

  if (!groups.length) {
    return (
      <div className="h-[3rem] mx-auto lg:text-[15px] text-[15px] text-[#333333] relative mt-[1rem] text-center">
        No data found
      </div>
    );
  }

  const formatCell = (row, key) => {
    if (key === 'name') {
      if (!row.investorId) return <ShareholderName name={row.name} />;
      return (
        <NavLink
          target='_blank'
          to={mappedPrimaryMasterId === 'vc' ? routeNames.vcInvestorById(row.investorId) : routeNames.peInvestorById(row.investorId)}
          className="text-[#A5711A] hover:text-[#A5711A] hover:font-semibold transition-colors duration-150"
        >
          <ShareholderName name={row.name} />
        </NavLink>
      );
    }
    if (key === 'stake') return `${row.stake.toFixed(2)}%`;
    return row[key] ? numFormatter(row[key]) : '-';
  };

  return (
    <>
    <div className='overflow-x-auto scrollbar'>
      <table className=" datatable w-full">
        <thead className="text-[12pt] leading-[15pt] bg-[#EDEDED] lg:text-[13px] lg:leading-[15px] text-[#666666] font-bold py-4 px-0 text-center ">
          <tr>
            {columns.map((col) => (
              <th key={col.key} className={`py-3.5 px-5 whitespace-nowrap ${col.className}`}>{col.label}</th>
            ))}
          </tr>
        </thead>
        <tbody className="text-[12pt] leading-[16pt] lg:text-[14px] text-center">
          {groups.map((group) => {
            const expanded = expandedGroups.includes(group.name);
            return (
              <React.Fragment key={group.name}>
                <tr onClick={() => toggleGroup(group.name)} className="font-bold cursor-pointer border-b font-sans_semi_bold">
                  {columns.map((col) => (
                    <td key={col.key} className={`border-b text-[#666666] data-td-animate whitespace-nowrap px-5 py-4 ${col.className}`}>
                      {col.key === 'name'
                        ? `${expanded ? '-' : '+'} ${group.name}`
                        : formatCell(group.summary, col.key)}
                    </td>
                  ))}
                </tr>
                {expanded && group.rows.map((row) => (
                  <tr key={`${group.name}-${row.name}`}>
                    {columns.map((col) => (
                      <td
                        key={col.key}
                        className={`border-b text-[#666666] data-td-animate whitespace-nowrap px-5 py-4 ${col.className} ${col.key === 'name' ? 'pl-[2rem]' : ''}`}
                      >
                        {formatCell(row, col.key)}
                      </td>
                    ))}
                  </tr>
                ))}
              </React.Fragment>
            );
          })}
          <tr className="font-bold border-b font-sans_semi_bold">
            {columns.map((col) => (
              <td
                key={col.key}
                className={`border-b text-[#666666] data-td-animate whitespace-nowrap px-5 py-4 ${col.className} ${col.key === 'name' ? 'pl-[2rem]' : ''}`}
              >
                {formatCell(totalRow, col.key)}
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
    {hasCcd && <div className="px-5 py-4 text-left pl-[2rem] text-xs font-medium"><span>Note: {CCD_NOTE}</span></div>}
    </>
  );
};

export default CfsFinancialsCapTable;
